/**
 * The portfolio calendar's date windows (CAP-1416): the timeline scrolls
 * without end, and its days are read 21 at a time.
 *
 * Days here are day numbers (whole days since 1 Jan 1970, counted on the
 * calendar, not the clock), so window arithmetic is integer arithmetic and the
 * clocks changing never moves a day.
 *
 * Windows are fixed on the calendar, not on wherever the grid opened: window
 * `n` is always the same 21 days (three Monday-to-Sunday weeks). The same date
 * is always read by the same request, so a window read once is a cache hit
 * from any scroll position, and two windows never overlap.
 */

export const WINDOW_DAYS = 21

const MS_PER_DAY = 86_400_000

/** Mon 5 Jan 1970: the day window 0 starts on, so every window starts on a Monday. */
const WINDOW_EPOCH_DAY = 4

/** `YYYY-MM-DD` as a day number, or null for anything that is not a real date. */
export function dayNumber(iso: string | null | undefined): number | null {
  const match = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const time = Date.UTC(year, month - 1, day)
  const date = new Date(time)
  // Rejects 2026-02-30 instead of quietly rolling it over to 2 March.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1) return null
  return time / MS_PER_DAY
}

/** A day number as `YYYY-MM-DD`. */
export function dayIso(day: number): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10)
}

/** The local calendar date of `date` (today, say) as a day number. */
export function dayOfDate(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY
}

/** The window a day falls in. */
export function windowOf(day: number): number {
  return Math.floor((day - WINDOW_EPOCH_DAY) / WINDOW_DAYS)
}

/** The first day of a window. */
export function windowStartDay(window: number): number {
  return WINDOW_EPOCH_DAY + window * WINDOW_DAYS
}

/** The days a window reads, both ends included: what `GET /admin/calendar` is asked for. */
export function windowRange(window: number): { from: string; to: string } {
  // Asked for every window on the grid on every render the grid scrolls by a
  // day: worked out once per window.
  let range = ranges.get(window)
  if (!range) {
    const start = windowStartDay(window)
    range = { from: dayIso(start), to: dayIso(start + WINDOW_DAYS - 1) }
    ranges.set(window, range)
  }
  return range
}

const ranges = new Map<number, { from: string; to: string }>()

/** A run of windows, both ends included. */
export interface WindowSpan {
  first: number
  last: number
}

/** The windows the days on screen fall in: read first, before anything else. */
export function visibleWindows(firstDay: number, lastDay: number): WindowSpan {
  return { first: windowOf(firstDay), last: windowOf(lastDay) }
}

/**
 * How near the days on screen may come to a day that is not read yet before it
 * is asked for: one window. A window takes about 1.5 to 2.2 s to read, and at
 * a normal scroll speed 21 days take longer than that to cross, so the edge is
 * read before it is reached.
 */
export const PREFETCH_DAYS = WINDOW_DAYS

/** The windows to read: those on screen, and any within one window of them. */
export function wantedWindows(firstDay: number, lastDay: number): WindowSpan {
  return { first: windowOf(firstDay - PREFETCH_DAYS), last: windowOf(lastDay + PREFETCH_DAYS) }
}

/**
 * The grid's days run at least this many windows past the windows on screen,
 * on each side. The ones beyond what is read show the loading skeleton, so a
 * fast fling meets placeholder days, never the end of the grid.
 */
export const RUNWAY_WINDOWS = 2

/**
 * ...and at most this many: a window further than this from the screen leaves
 * the grid (its layout, its cache entry once unused), and is read again if the
 * grid comes back to it. This is what keeps a long session's memory flat.
 */
export const KEEP_WINDOWS = 6

/**
 * The windows the grid spans, given the ones it spanned and the days now on
 * screen. It grows to keep `RUNWAY_WINDOWS` either side of the screen and drops
 * windows more than `KEEP_WINDOWS` away; in between it stays as it is, so the
 * grid's first day (and with it every position on it) changes only once every
 * few weeks of scrolling, not every frame.
 *
 * Returns `range` itself when nothing changes, so a caller can compare by
 * identity. A jump far away (Today, a link) starts a new span around it.
 */
export function nextWindowSpan(
  range: WindowSpan | null,
  firstDay: number,
  lastDay: number
): WindowSpan {
  const screen = visibleWindows(firstDay, lastDay)
  const least = { first: screen.first - RUNWAY_WINDOWS, last: screen.last + RUNWAY_WINDOWS }
  if (
    !range ||
    range.last < screen.first - KEEP_WINDOWS ||
    range.first > screen.last + KEEP_WINDOWS
  ) {
    return least
  }
  const first = Math.max(Math.min(range.first, least.first), screen.first - KEEP_WINDOWS)
  const last = Math.min(Math.max(range.last, least.last), screen.last + KEEP_WINDOWS)
  return first === range.first && last === range.last ? range : { first, last }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * The days on screen as the toolbar says them: "27 Oct - 14 Nov 2026", or
 * "28 Dec 2026 - 17 Jan 2027" across a new year.
 */
export function rangeLabel(firstDay: number, lastDay: number): string {
  const first = new Date(firstDay * MS_PER_DAY)
  const last = new Date(lastDay * MS_PER_DAY)
  const sameYear = first.getUTCFullYear() === last.getUTCFullYear()
  const start = `${first.getUTCDate()} ${MONTHS[first.getUTCMonth()]}${sameYear ? '' : ` ${first.getUTCFullYear()}`}`
  return `${start} - ${last.getUTCDate()} ${MONTHS[last.getUTCMonth()]} ${last.getUTCFullYear()}`
}
